import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Highlight, themes } from 'prism-react-renderer';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface CodeBlockProps {
  code: string;
  language?: string;
  title?: string;
  showLineNumbers?: boolean;
  className?: string;
}

// Map common language aliases to Prism language keys
const languageMap: Record<string, string> = {
  js: 'javascript',
  ts: 'typescript',
  tsx: 'tsx',
  jsx: 'jsx',
  json: 'json',
  xml: 'markup',
  html: 'markup',
  bash: 'bash',
  sh: 'bash',
  shell: 'bash',
  yaml: 'yaml',
  yml: 'yaml',
  plaintext: 'plain',
  text: 'plain',
};

export function CodeBlock({ 
  code, 
  language = 'bash', 
  title,
  showLineNumbers = false,
  className 
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  
  const prismLanguage = languageMap[language.toLowerCase()] || language;
  const theme = resolvedTheme === 'dark' ? themes.vsDark : themes.github;

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn('relative group rounded-lg overflow-hidden border border-border bg-muted/50', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 md:px-4 py-2 bg-muted border-b border-border gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {title && <span className="text-xs md:text-sm font-medium text-foreground truncate">{title}</span>}
          <span className="text-xs text-muted-foreground uppercase tracking-wider shrink-0">{language}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={copyToClipboard}
          className="h-7 px-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-500" />
              <span className="text-xs ml-1 hidden sm:inline">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span className="text-xs ml-1 hidden sm:inline">Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Code with syntax highlighting */}
      <Highlight theme={theme} code={code.trim()} language={prismLanguage}>
        {({ className: highlightClass, style, tokens, getLineProps, getTokenProps }) => (
          <div className="overflow-x-auto">
            <pre 
              className={cn('p-3 md:p-4 text-xs md:text-sm font-mono', highlightClass)} 
              style={{ ...style, backgroundColor: 'transparent' }}
            >
              {showLineNumbers ? (
                <table className="w-full">
                  <tbody>
                    {tokens.map((line, i) => {
                      const lineProps = getLineProps({ line });
                      return (
                        <tr key={i} className={lineProps.className} style={lineProps.style}>
                          <td className="pr-3 md:pr-4 text-muted-foreground select-none text-right w-6 md:w-8 align-top">
                            {i + 1}
                          </td>
                          <td className="whitespace-pre">
                            {line.map((token, key) => {
                              const tokenProps = getTokenProps({ token });
                              return <span key={key} className={tokenProps.className} style={tokenProps.style}>{tokenProps.children}</span>;
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <code>
                  {tokens.map((line, i) => {
                    const lineProps = getLineProps({ line });
                    return (
                      <div key={i} className={lineProps.className} style={lineProps.style}>
                        {line.map((token, key) => {
                          const tokenProps = getTokenProps({ token });
                          return <span key={key} className={tokenProps.className} style={tokenProps.style}>{tokenProps.children}</span>;
                        })}
                      </div>
                    );
                  })}
                </code>
              )}
            </pre>
          </div>
        )}
      </Highlight>
    </div>
  );
}
